import {useCallback, useMemo, useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {useForm, Resolver} from 'react-hook-form';
import {AdminProfileFormData} from './admin-profile-interface';
import {AdminProfileValidator} from './admin-profile-validator';
import {handleCaptureLogin} from '@utils/firebase-request';
import {usePostHog} from 'posthog-js/react';
import {GoogleAuthRequestImpl} from './admin-profile-request';
import {GoogleAuthController} from './admin-profile-controller';
import {useStoreContext} from '../../../store/store-context';
import {symbolRegex} from '@constants';
import {usePostHogHook} from '@hooks/post-hog';

export function useAdminProfileHook(
  onSubmit: (data: AdminProfileFormData) => Promise<void>,
) {
  const {
    register,
    handleSubmit,
    errors,
    formState,
    control,
    setValue,
    watch,
  } = useForm<AdminProfileFormData>({
    resolver: yupResolver(AdminProfileValidator) as Resolver<
      AdminProfileFormData,
      object
    >,
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      firstName: '',
      phoneNumber: '',
      lastName: '',
      password: '',
      countryCode: '+1',
    },
  });

  const submitForm = useCallback(onSubmit, [onSubmit]);

  const [showPasswordValidator, setShowPasswordValidator] = useState(false);

  const [validationCriteria, setValidationCriteria] = useState([
    {
      label: 'At least 8 characters',
      completed: false,
    },
    {
      label: 'At least one uppercase letter',
      completed: false,
    },
    {
      label: 'At least one lowercase letter',
      completed: false,
    },
    {
      label: 'At least one special character',
      completed: false,
    },
  ]);

  const [isDisabled, setIsDisabled] = useState(true);
  const {email, firstName, lastName, password} = watch();

  useMemo(() => {
    const isPasswordValid = validationCriteria.every(
      (criteria) => criteria.completed,
    );
    if (email && firstName && lastName && password && isPasswordValid)
      setIsDisabled(false);
    else setIsDisabled(true);
  }, [email, firstName, lastName, password, validationCriteria]);

  const isSubmitting = useMemo(() => {
    return formState.isSubmitting;
  }, [formState]);

  const hasError = useMemo(() => {
    return formState.submitCount > 0 && formState.errors;
  }, [formState.errors, formState.submitCount]);

  const googleAuthRequest = new GoogleAuthRequestImpl();
  const googleAuthController = new GoogleAuthController(googleAuthRequest);
  const handleSetPrefix = (prefix: string) => {
    setValue('countryCode', prefix);
  };

  const validatePassword = (password: string) => {
    const updatedCriteria = validationCriteria.map((criterion) => {
      let completed = false;

      if (criterion.label === 'At least 8 characters') {
        completed = password.length >= 8;
      } else if (criterion.label === 'At least one uppercase letter') {
        completed = /[A-Z]/.test(password);
      } else if (criterion.label === 'At least one lowercase letter') {
        completed = /[a-z]/.test(password);
      } else if (criterion.label === 'At least one special character') {
        // eslint-disable-next-line no-useless-escape
        completed = symbolRegex.test(password);
      }

      return {
        ...criterion,
        completed,
      };
    });

    setValidationCriteria(updatedCriteria);
  };

  const handlePasswordChange = (value: string) => {
    if (value.length > 0 && !showPasswordValidator)
      setShowPasswordValidator(true);
    validatePassword(value);
    setValue('password', value);
  };

  const {postHogCapture} = usePostHogHook();
  const postHog = usePostHog();

  const {authStore} = useStoreContext();

  const authGoogle = (authObject: any) => {
    const authed__ = async (token: string, isAdmin: boolean) => {
      const response: any = await googleAuthController.googleAuth(
        token,
        isAdmin,
      );
      if (response) {
        if (response.redirectUrl) {
          localStorage.setItem('initProductTour', 'true');
          window.location.href = response.redirectUrl;
        } else {
          authStore.setAuthResponse(response);

          postHog?.identify(response.user.id, {
            email: response.user.email,
            workspace: response.user.workspace,
          });

          const userReturnAfter14Days = () =>
            postHogCapture(
              `14_days_return -  User returns after 14 days`,
              response.user.workspace,
            );

          postHogCapture('workspace', response.user.workspace);

          handleCaptureLogin(
            {
              userId: response.user.id,
              workspaceId: response.user.workspace.id,
              userCreatedAt: response.user.createdAt,
            },
            userReturnAfter14Days,
          );
        }
      }
    };

    authed__(authObject.tokenId, true);
  };

  return {
    register,
    onSubmit,
    errors,
    isSubmitting,
    submitForm,
    handleSubmit,
    hasError,
    handleSetPrefix,
    isDisabled,
    handlePasswordChange,
    showPasswordValidator,
    validationCriteria,
    authGoogle,
    control,
  };
}
